import type { SelectChangeEvent } from '@mui/material';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Box, Modal, Typography, useTheme, Select, MenuItem } from '@mui/material';
import { Button } from '@components/index';
import { SecureSubmitButton } from '@components/buttons/SecureSubmitButton';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import { XIcon } from '@icons/index';
import { useUpdateTenantConfig } from '@queryHooks/useTenantConfigs';
import { getErrorMessage } from '@locales/i18nErrorMessage';

interface MfaPolicyModalProps {
  open: boolean;
  tenantId: string;
  onClose: () => void;
  mfaEnabled: boolean;
}

export const MfaPolicyModal = ({ open, tenantId, onClose, mfaEnabled }: MfaPolicyModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    uiStore: { toastStore },
  } = useStore();

  const [selectedOption, setSelectedOption] = useState<'REQUIRED' | 'OPTIONAL'>(mfaEnabled ? 'REQUIRED' : 'OPTIONAL');

  const { mutate: updateTenantConfig, isPending } = useUpdateTenantConfig();

  useEffect(() => {
    if (open) {
      setSelectedOption(mfaEnabled ? 'REQUIRED' : 'OPTIONAL');
    }
  }, [open, mfaEnabled]);

  const handleClose = () => {
    onClose();
  };

  const handleSave = () => {
    updateTenantConfig(
      {
        tenantId,
        configKey: 'ENABLE_MFA',
        configValue: selectedOption === 'REQUIRED' ? 'true' : 'false',
      },
      {
        onSuccess: () => {
          toastStore.open({
            message: t('Security.mfaPolicy.modalSuccess'),
          });
          onClose();
        },
        onError: error => {
          const errorInfo = getErrorMessage(error);
          toastStore.open({
            message: errorInfo.message || t('Security.mfaPolicy.modalError'),
          });
        },
      },
    );
  };

  const handleSelectChange = (event: SelectChangeEvent<unknown>) => {
    setSelectedOption(event.target.value as 'REQUIRED' | 'OPTIONAL');
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalContainer>
        <DialogHeader>
          <HeaderTitle variant="headline6">{t('Security.mfaPolicy.modalTitle')}</HeaderTitle>
          <CloseButton onClick={handleClose}>
            <XIcon width={24} height={24} color={theme.colors['ic-gray-main']} />
          </CloseButton>
        </DialogHeader>

        <DialogContent>
          <OptionSection>
            <OptionBox>
              <OptionLabel>{t('Security.mfaPolicy.authMethod')}</OptionLabel>
              <OptionValue>{t('Security.mfaPolicy.otp')}</OptionValue>
            </OptionBox>

            <RequiredSection>
              <RequiredLabel>{t('Security.mfaPolicy.required')}</RequiredLabel>
              <StyledSelect value={selectedOption} onChange={handleSelectChange}>
                <MenuItem value="REQUIRED">{t('Security.option.required')}</MenuItem>
                <MenuItem value="OPTIONAL">{t('Security.option.optional')}</MenuItem>
              </StyledSelect>
            </RequiredSection>
          </OptionSection>
        </DialogContent>

        <DialogFooter>
          <CustomButton variant="outline" size="extraLarge" onClick={handleClose} paddingHorizontal={24}>
            {t('GNB_Profile_MyProfile_11')}
          </CustomButton>
          <CustomSecureButton isPending={isPending} variant="contain" size="extraLarge" onClick={handleSave} paddingHorizontal={24}>
            {t('Common.button.save')}
          </CustomSecureButton>
        </DialogFooter>
      </ModalContainer>
    </Modal>
  );
};

const ModalContainer = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 480px;
  background-color: ${({ theme }) => theme.colors['bg-white']};
  border-radius: 6px;
  box-shadow: 0px 8px 10px -5px rgba(0, 0, 0, 0.08), 0px 16px 24px 2px rgba(0, 0, 0, 0.04),
    0px 6px 30px 5px rgba(0, 0, 0, 0.05);
  outline: none;
`;

const DialogHeader = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  height: 60px;
  padding: 18px 28px;
  border-bottom: none;
`;

const HeaderTitle = styled(Typography)`
  ${fonts.Headline6}
  color: ${({ theme }) => theme.colors['text-gray-main']};
` as typeof Typography;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }
`;

const DialogContent = styled(Box)`
  padding: 14px 24px 0px;
`;

const OptionSection = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const OptionBox = styled(Box)`
  display: flex;
  flex-direction: row;
  gap: 6px;
  padding: 12px;
  background-color: ${({ theme }) => theme.colors['bg-gray-light']};
  border-radius: 3px;
`;

const OptionLabel = styled(Typography)`
  ${fonts.Headline8}
  color: ${({ theme }) => theme.colors['text-gray-main']};
  width: 100px;
  flex-shrink: 0;
` as typeof Typography;

const OptionValue = styled(Typography)`
  ${fonts.Button4}
  color: ${({ theme }) => theme.colors['text-gray-main']};
  flex: 1;
` as typeof Typography;

const RequiredSection = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const RequiredLabel = styled(Typography)`
  ${fonts.Headline8}
  color: ${({ theme }) => theme.colors['text-gray-main']};
  display: flex;
  align-items: center;
  gap: 2px;

  &::after {
    content: '*';
    color: ${({ theme }) => theme.colors['text-red']};
    margin-left: 2px;
  }
` as typeof Typography;

const StyledSelect = styled(Select)`
  width: 100%;
  height: 44px;
  ${fonts.Button4}

  & .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) => theme.colors['border-gray-light']};
    border-radius: 2px;
  }

  &:hover .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) => theme.colors['border-gray-dark']};
  }

  &.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) => theme.colors['border-primary']};
  }

  & .MuiSelect-select {
    padding: 6px 8px 6px 12px;
    ${fonts.Button4}
    color: ${({ theme }) => theme.colors['text-gray-main']};
  }
`;

const DialogFooter = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 24px 68px;
  margin-top: 17px;
`;

const CustomButton = styled(Button)`
  width: 168px;
`;

const CustomSecureButton = styled(SecureSubmitButton)`
  width: 168px;
`;
